import type { DataTableColumn } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { HatTextTooltip } from "@/components/tooltip";
import type { QueryHotspotTableRow } from "./QueryHotspotTableRow";
import styles from "./ObservabilityPage.module.css";

/** 누적 게이지 열 머리의 설명이다. 세 열이 같은 문장을 쓴다. */
const CUMULATIVE_HINT =
  "통계 리셋 이후 누적으로 계산된 값이라 창을 좁혀도 잘 줄지 않습니다. 왼쪽 세 열(이 구간)과 같은 구간이 아닙니다.";

/**
 * 쿼리 hotspot 열이다. **이 구간** 열 세 개와 **누적 게이지** 열 세 개를 구분해 표시한다.
 *
 * 두 묶음은 같은 "ms" 를 쓰지만 뜻이 다르다. 왼쪽 세 열은 창을 바꾸면 따라 바뀐다. 오른쪽 세
 * 열은 통계 리셋 이후의 누적이라 창을 좁혀도 그대로다. 구분하지 않으면 "1시간 창인데 최댓값이
 * 3초" 를 그 1시간에 일어난 일로 읽는다. 그러면 창을 좁혀도 줄지 않는 것을 버그로 신고한다.
 *
 * 누적 열을 빼지 않는 이유: 평균만으로는 "항상 느린 쿼리" 와 "가끔 크게 튀는 쿼리" 를 구분할
 * 수 없다. 두 경우는 조치 방향이 반대다.
 */
export function createQueryHotspotColumns(): readonly DataTableColumn<QueryHotspotTableRow>[] {
  return [
    {
      key: "queryId",
      header: "fingerprint",
      width: "minmax(180px, 1fr)",
      render: (row) => <FingerprintCell row={row} />,
    },
    {
      key: "windowedCalls",
      header: "호출",
      width: "100px",
      align: "right",
      render: (row) => <span className={styles.numeric}>{row.windowedCalls}</span>,
    },
    {
      key: "windowedTotalMs",
      header: "총 시간",
      width: "120px",
      align: "right",
      render: (row) => <span className={styles.numeric}>{row.windowedTotalMs}</span>,
    },
    {
      key: "windowedMeanMs",
      header: "구간 평균",
      width: "120px",
      align: "right",
      render: (row) => <span className={styles.numeric}>{row.windowedMeanMs}</span>,
    },
    {
      // 구간 열이 여기서 끝나고 누적 열이 시작된다. 첫 누적 열이 경계선을 가진다.
      key: "cumulativeMinMs",
      header: <CumulativeHeader label="최소" boundary />,
      width: "128px",
      align: "right",
      render: (row) => (
        <span className={`${styles.groupBoundary} ${styles.numeric}`}>{row.cumulativeMinMs}</span>
      ),
    },
    {
      key: "cumulativeMaxMs",
      header: <CumulativeHeader label="최대" />,
      width: "120px",
      align: "right",
      render: (row) => <span className={styles.numeric}>{row.cumulativeMaxMs}</span>,
    },
    {
      key: "cumulativeStddevMs",
      header: <CumulativeHeader label="산포" />,
      width: "120px",
      align: "right",
      render: (row) => <span className={styles.numeric}>{row.cumulativeStddevMs}</span>,
    },
    {
      key: "sharedBlocksRead",
      header: "shared block read",
      width: "150px",
      align: "right",
      render: (row) => <span className={styles.numeric}>{row.sharedBlocksRead}</span>,
    },
  ];
}

/**
 * fingerprint 칸이다. 십진수를 그대로 표시하고 hex 를 툴팁에 추가한다.
 *
 * 십진수가 기준인 이유: 조회가 `WHERE queryid = <십진수>` 이기 때문이다. 사용자가 복사해 바로
 * 쓸 값을 화면에 둔다. 눈으로 비교하기 쉬운 16자 hex 는 툴팁에 표시한다.
 */
function FingerprintCell({ row }: { row: QueryHotspotTableRow }): ReactNode {
  const hex = row.queryIdHex === "" ? "" : ` · ${row.queryIdHex}`;
  return (
    <HatTextTooltip
      text={`pg_stat_statements 의 queryid 입니다. SQL 원문은 수집하지 않습니다${hex}`}
    >
      <span className={styles.fingerprint}>{row.queryId}</span>
    </HatTextTooltip>
  );
}

/** 누적 게이지 열 머리다. 라벨에 값 종류를 붙이고 톤을 낮춰 구간 열과 구분한다. */
function CumulativeHeader({
  label,
  boundary = false,
}: {
  label: string;
  boundary?: boolean;
}): ReactNode {
  return (
    <HatTextTooltip text={CUMULATIVE_HINT} align="end">
      <span
        className={
          boundary ? `${styles.groupBoundary} ${styles.cumulativeHeader}` : styles.cumulativeHeader
        }
      >
        {label} (누적)
      </span>
    </HatTextTooltip>
  );
}
